import { BaseService } from './base-service';
import { BeraSumRPN } from '@/core/domain/bera-sum-rpn';
import { BeraSixToo } from '@/core/domain/bera-six-too';
import { BeraSumRPNMapper } from '@/core/mapper/bera-sum-rpn';
import { BeraSixTooMapper } from '@/core/mapper/bera-six-too';
import { BeraComparison } from '@/core/domain/bera-comparison';
import { BeraGenderNeutralStation } from '@/core/domain/bera-gns';
import { BeraComparisonMapper } from '@/core/mapper/bera-comparison';
import { BeraGenderNeutralStationMapper } from '@/core/mapper/bera-gns';
import { BeraSumRPNDTO, BeraSumRPNRequestDTO } from '@/core/dto/bera-sum-rpn';
import { BeraSixTooDTO, BeraSixTooRequestDTO } from '@/core/dto/bera-six-too';
import { BeraComparisonDTO, GetBeraComparisonRequestDTO } from '@/core/dto/bera-comparison';
import { BeraGenderNeutralStationGeneratedDTO, BeraGenderNeutralStationRequestDTO } from '@/core/dto/bera-gns';
import {
	SetKeyResultRequestDTO,
	SetKeyResultResponseDTO,
	GetStepKeyResultsRequestDTO,
	GetStepKeyResultsResponseDTO,
	UpdateReportRequestDTO,
	UpdateReportResponseDTO,
	GetSummaryResultRequestDTO,
	GetSummaryResultResponseDTO,
	GetReportResultRequestDTO,
	GetReportResultResponseDTO,
	GetNoENSTasksRequestDTO,
	GetNoENSTasksResponseDTO,
	GetActionPlansRequestDTO,
	GetActionPlansResponseDTO,
	GetENSWorkstationsRequestDTO,
	GetENSWorkstationsResponseDTO,
	GetNOENSWorkstationsRequestDTO,
	GetNOENSWorkstationsResponseDTO,
	GetDashboardENSRequestDTO,
	GetDashboardENSResponseDTO,
	ConsolidateReportRequestDTO,
	ConsolidateReportResponseDTO
} from '@/core/dto/bera';

class BeraService extends BaseService<BeraComparisonDTO> {
	constructor(public readonly basePath: string = '/bera') {
		super();
	}

	async getHierarchySumRpn(params: GetBeraComparisonRequestDTO): Promise<BeraComparison[]> {
		const url = this.basePath + '/report/dashboard/hierarchy-sum-rpn';
		const { data } = await this.getInstance().get<BeraComparisonDTO>(url, { params });
		return new BeraComparisonMapper(data).toDomain();
	}

	async getSixTooSumRPN(params: BeraSixTooRequestDTO): Promise<BeraSixToo> {
		const url = this.basePath + '/report/dashboard/six-too-sum-rpn';
		const { data } = await this.getInstance().get<BeraSixTooDTO>(url, { params });
		return new BeraSixTooMapper(data).toDomain();
	}

	async getSumRPN(params: BeraSumRPNRequestDTO): Promise<BeraSumRPN> {
		const url = this.basePath + '/report/dashboard/sum-rpn';
		const { data } = await this.getInstance().get<BeraSumRPNDTO>(url, { params });
		return new BeraSumRPNMapper(data).toDomain();
	}

	async genderNeutral(params: BeraGenderNeutralStationRequestDTO): Promise<BeraGenderNeutralStation> {
		const url = this.basePath + '/report/dashboard/gender-neutral';
		const { data } = await this.getInstance().get<BeraGenderNeutralStationGeneratedDTO>(url, { params });
		return new BeraGenderNeutralStationMapper(data.report, data.labels).toDomain();
	}

	async setKeyResult(body: SetKeyResultRequestDTO): Promise<SetKeyResultResponseDTO> {
		const url = this.basePath + `/report/set-key-result`;
		const { data } = await this.getInstance().post<SetKeyResultResponseDTO>(url, body);
		return data;
	}

	async getStepKeyResult(params: GetStepKeyResultsRequestDTO): Promise<GetStepKeyResultsResponseDTO> {
		const url = `${this.basePath}/report/step-key-result`;
		const { data } = await this.getInstance().get<GetStepKeyResultsResponseDTO>(url, { params });
		return data;
	}

	async updateReport(body: UpdateReportRequestDTO): Promise<UpdateReportResponseDTO> {
		const url = `${this.basePath}/report/${body.id}`;
		const { data } = await this.getInstance().put<UpdateReportResponseDTO>(url, body);
		return data;
	}

	async getSummaryResult(params: GetSummaryResultRequestDTO): Promise<GetSummaryResultResponseDTO> {
		const url = `${this.basePath}/job-summary/result/${params.id}`;
		const { data } = await this.getInstance().get<GetSummaryResultResponseDTO>(url, { params });
		return data;
	}

	async getReportResult(params: GetReportResultRequestDTO): Promise<GetReportResultResponseDTO> {
		const url = `${this.basePath}/report/result/${params.file_id}`;
		const { data } = await this.getInstance().get<GetReportResultResponseDTO>(url, { params });
		return data;
	}

	async getNoENSTasks(params: GetNoENSTasksRequestDTO): Promise<GetNoENSTasksResponseDTO> {
		const url = `${this.basePath}/job-summary/no-ens-tasks/${params.id}`;
		const { data } = await this.getInstance().get<GetNoENSTasksResponseDTO>(url, { params });
		return data;
	}

	async getActionPlans(params: GetActionPlansRequestDTO): Promise<GetActionPlansResponseDTO> {
		const url = `${this.basePath}/job-summary/action-plans/${params.id}`;
		const { data } = await this.getInstance().get<GetActionPlansResponseDTO>(url, { params });
		return data;
	}

	async consolidate(params: ConsolidateReportRequestDTO): Promise<ConsolidateReportResponseDTO> {
		const url = `${this.basePath}/job-summary/${params.id}/consolidated`;
		await this.getInstance().put(url, params);
	}

	async getENSWorkstations(params: GetENSWorkstationsRequestDTO): Promise<GetENSWorkstationsResponseDTO> {
		const url = `${this.basePath}/job-summary/ens-workstations`;
		const { data } = await this.getInstance().get<GetENSWorkstationsResponseDTO>(url, { params });
		return data;
	}

	async getNOENSWorkstations(params: GetNOENSWorkstationsRequestDTO): Promise<GetNOENSWorkstationsResponseDTO> {
		const url = `${this.basePath}/job-summary/no-ens-workstations`;
		const { data } = await this.getInstance().get<GetNOENSWorkstationsResponseDTO>(url, { params });
		return data;
	}

	async getDashboardENS(params: GetDashboardENSRequestDTO): Promise<GetDashboardENSResponseDTO> {
		const url = `${this.basePath}/job-summary/dashboard-ens`;
		const { data } = await this.getInstance().get<GetDashboardENSResponseDTO>(url, { params });
		return data;
	}
}

const Service = Object.freeze(new BeraService());
export { Service };
